import React from 'react';
import { Download, RefreshCw, Users, UserCheck, X, CheckSquare, Mail, MessageCircle } from 'lucide-react';
import { PhysicalInvitationStats } from '../types';

type TabType = 'overview' | 'attendees' | 'declined' | 'physical-invitations' | 'messages';

interface TabNavigationProps {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  attendeesCount: number;
  declinedCount: number;
  physicalInvitationStats: PhysicalInvitationStats;
  messagesCount: number;
  batchEditMode: boolean;
  toggleBatchEditMode: () => void;
  loading: boolean;
  exportingData: boolean;
  onRefresh: () => void;
  onShowMainExport: () => void;
}

export const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  setActiveTab,
  attendeesCount,
  declinedCount,
  physicalInvitationStats,
  messagesCount,
  batchEditMode,
  toggleBatchEditMode,
  loading,
  exportingData,
  onRefresh,
  onShowMainExport,
}) => {
  const tabClass = (tab: TabType) =>
    `min-w-[100px] justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
      activeTab === tab
        ? 'bg-wedding-pink text-white shadow-sm'
        : 'text-wedding-text-body hover:text-wedding-pink hover:bg-wedding-pink/5'
    }`;

  return (
    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
      <div className="overflow-x-auto">
        <div className="flex space-x-1 bg-wedding-pink/10 rounded-lg p-1 min-w-max">
          <button onClick={() => setActiveTab('overview')} className={tabClass('overview')}>
            <Users className="h-4 w-4 mr-2 inline" />
            統計概覽
          </button>
          <button onClick={() => setActiveTab('attendees')} className={tabClass('attendees')}>
            <UserCheck className="h-4 w-4 mr-2 inline" />
            出席者 ({attendeesCount})
          </button>
          <button onClick={() => setActiveTab('declined')} className={tabClass('declined')}>
            <X className="h-4 w-4 mr-2 inline" />
            不出席者 ({declinedCount})
          </button>
          <button onClick={() => setActiveTab('physical-invitations')} className={tabClass('physical-invitations')}>
            <Mail className="h-4 w-4 mr-2 inline" />
            實體喜帖 ({physicalInvitationStats.totalRequests})
          </button>
          <button onClick={() => setActiveTab('messages')} className={tabClass('messages')}>
            <MessageCircle className="h-4 w-4 mr-2 inline" />
            賓客留言 ({messagesCount})
          </button>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        {activeTab !== 'overview' && (
          <button
            onClick={toggleBatchEditMode}
            className={`px-4 py-2 border rounded-md flex items-center space-x-1 transition-colors ${
              batchEditMode
                ? 'bg-wedding-pink text-white border-wedding-pink hover:bg-wedding-pink/90'
                : 'border-wedding-pink/30 bg-wedding-bg hover:bg-wedding-pink/5 text-wedding-text-body'
            }`}
          >
            <CheckSquare className="h-4 w-4" />
            <span>{batchEditMode ? '取消批次編輯' : '批次編輯'}</span>
          </button>
        )}

        <button
          onClick={onRefresh}
          disabled={loading}
          className="px-4 py-2 border border-wedding-pink/30 bg-wedding-bg hover:bg-wedding-pink/5 flex items-center space-x-1 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>刷新</span>
        </button>
        <button
          onClick={onShowMainExport}
          disabled={exportingData}
          className="px-4 py-2 border border-wedding-pink/30 bg-wedding-bg hover:bg-wedding-pink/5 flex items-center space-x-1 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          <span>{exportingData ? '匯出中...' : '匯出資料'}</span>
        </button>
      </div>
    </div>
  );
};
